import { graphql } from 'gatsby';
import React, { useEffect, useRef, useState } from 'react';
import careerDefinitions from '~/data/sidebar/career_data.json';
import BunnyFilter from './filter';

type BlockProps = {
  block: {
    _template: string;
    css_class_name: string;
    settings: any;
    job_info: JobInfo[];
  };
};

export type JobInfo = {
  is_active: boolean;
  text: string;
  type: string;
  badge?: string;
  title: string;
  cta_url: string;
  tags: JobInfoTag[];
};

type JobInfoTag = {
  job_tag: string;
};

const CareerCards: React.FC<BlockProps> = ({ block }) => {
  const DEFAULT_ALL = 'All';
  const [activeJobType, setActiveJobType] = useState(DEFAULT_ALL);
  const openPositions = block.job_info.filter(item => item.is_active);
  const options: { label: string; name: string }[] = [
    {
      label: DEFAULT_ALL,
      name: DEFAULT_ALL,
    },
  ];
  careerDefinitions.source__job_type.forEach(type => {
    if (openPositions.some(item => item.type === type)) {
      options.push({
        label: type,
        name: type.toLowerCase(),
      });
    }
  });
  const byActiveType = (item: JobInfo) =>
    activeJobType === DEFAULT_ALL ||
    item['type']?.toLowerCase() === activeJobType;

  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = window.location.href.match(/jobs=(.*)/);
      const jobParam = urlParams && urlParams[1];
      if (jobParam) {
        let jobType = jobParam.split('#')[0] || '';
        jobType = decodeURI(jobType);

        if (options.some(option => option.name.toLowerCase() === jobType)) {
          setActiveJobType(jobType.toLowerCase());
        }
      }
    }
  }, []);

  if (!openPositions.length) {
    return (
      <div className={`block ${block._template} ${block.css_class_name}`}>
        <div className='container'>
          <div className='flex w-full justify-center bg-[#fff] p-4 leading-normal border-rounded-soft'>
            <p>No job openings at the moment</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <BunnyFilter
        options={options}
        setActiveType={setActiveJobType}
        activeType={activeJobType}
        container={container}
      />
      <section className={`block ${block._template} ${block.css_class_name}`}>
        <div className='container'>
          <div
            className='career-cards-list'
            ref={container}
          >
            {openPositions.map((item, index) => {
              if (byActiveType(item)) {
                return (
                  <a
                    className='career-cards-list__item relative'
                    key={`opening-${index}`}
                    href={item.cta_url}
                  >
                    <CareerCard opening={item} />
                  </a>
                );
              } else {
                return null;
              }
            })}
          </div>
        </div>
      </section>
    </>
  );
};

const CareerCard: React.FC<{ opening: JobInfo }> = ({ opening }) => {
  return (
    <div className='career-cards-content'>
      <h3 className='career-cards-title'>
        <span className='relative'>
          <span>{opening.title}</span>
          {opening.badge && (
            <div className='coming-soon-label seniority-badge'>
              {opening.badge}
            </div>
          )}
        </span>
      </h3>
      <div
        className='career-cards-text'
        dangerouslySetInnerHTML={{ __html: opening.text }}
      />
      <ul className='career-cards-tags'>
        {opening.tags.map((tag, index) => (
          <li
            className='career-cards-tags__item'
            key={`opening-${index}`}
          >
            <JobTag name={tag.job_tag} />
          </li>
        ))}
      </ul>
    </div>
  );
};

const JobTag: React.FC<{ name: string }> = ({ name }) => {
  const tagInfo = careerDefinitions.job_tags.find(tag => tag.name === name);

  if (!tagInfo) {
    return null;
  }

  return (
    <>
      <img
        className='career-cards-tags__icon'
        src={tagInfo.image}
        alt={tagInfo.image_alt_text}
      />
      <div className='career-cards-tags__label'>{tagInfo.name}</div>
    </>
  );
};

export default CareerCards;

export const careerFragmentQuery = graphql`
  fragment CareerFragment on Block {
    job_info {
      is_active
      text
      type
      badge
      title
      cta_url
      tags {
        job_tag
      }
    }
  }
`;
